import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Status } from '@googlemaps/react-wrapper';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import MapContext from './MapContext.js';
import S from './MapView.css';
import Interaction from './Interaction.js';
import useLayerManager from './hooks/useLayerManager.js';
const DEFAULT_CENTER = {
  lat: 39.273002,
  lng: -76.614105
};
function MapView(props) {
  const {
    center = DEFAULT_CENTER,
    zoom = 15,
    bounds: initialBounds,
    mapId,
    mapTypeId = 'roadmap',
    layers,
    children,
    mapName = 'map',
    onLoad,
    pointer,
    interleavedLayers = false,
    className,
    style,
    mapOptions
  } = props;
  const [map, setMap] = useState();
  const [deckGlInstance, setDeckGlInstance] = useState();
  const mapRef = useRef();
  const interaction = useRef(new Interaction());
  useEffect(() => {
    if (map) {
      const cMapTypeId = map.getMapTypeId();
      if (mapTypeId !== cMapTypeId) {
        map.setMapTypeId(mapTypeId);
      }
    }
  }, [mapTypeId, map]);
  useEffect(() => {
    if (map && mapOptions) {
      map.setOptions(mapOptions);
    }
  }, [mapOptions, map]);
  useEffect(() => {
    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapId,
      mapTypeId,
      disableDefaultUI: true,
      streetViewControl: true,
      clickableIcons: false,
      streetViewControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_CENTER
      },
      ...mapOptions
    });
    if (initialBounds) {
      const [[west, south], [east, north]] = initialBounds;
      map.fitBounds({
        south,
        west,
        north,
        east
      });
    }
    window.addEventListener(`${mapName}-flyTo`, ({
      detail
    }) => {
      const {
        lat,
        lon,
        zoom: evtZoom,
        bounds,
        options
      } = detail;
      if (bounds) {
        const [[west, south], [east, north]] = bounds;
        map.fitBounds({
          south,
          west,
          north,
          east
        }, options?.padding);
        return;
      }
      if (lat != null && lon != null) {
        map.panTo({
          lat,
          lng: lon
        });
        if (evtZoom) {
          map.setZoom(evtZoom);
        }
      }
    });
    window.addEventListener(`${mapName}-zoomIn`, () => {
      const zoom = map.getZoom();
      map.setZoom(zoom + 1);
    });
    window.addEventListener(`${mapName}-zoomOut`, () => {
      const zoom = map.getZoom();
      map.setZoom(zoom - 1);
    });
    const instance = new GoogleMapsOverlay({
      useDevicePixels: true,
      interleaved: interleavedLayers,
      onHover: (info, evt) => {
        pointer.onMouseMove(info, evt, {
          map,
          deck: instance,
          interaction: interaction.current
        });
      },
      onClick: (info, evt) => {
        pointer.onMouseClick(info, evt, {
          map,
          deck: instance,
          interaction: interaction.current
        });
      }
    });
    instance.setMap(map);
    setDeckGlInstance(instance);
    setMap(map);
    onLoad?.(Status.SUCCESS, {
      map
    });
  }, []);
  useEffect(() => {
    if (deckGlInstance) {
      const interleaved = deckGlInstance.props.interleaved;
      if (interleaved !== interleavedLayers) {
        deckGlInstance.setProps({
          interleaved: interleavedLayers
        });
      }
    }
  }, [interleavedLayers, deckGlInstance]);
  const {
    layers: lmLayers,
    addLayers,
    removeLayers
  } = useLayerManager();
  useEffect(() => {
    if (deckGlInstance) {
      deckGlInstance.setProps({
        layers: lmLayers
      });
    }
  }, [lmLayers]);
  const contextValue = useMemo(() => {
    return {
      map,
      deckGlInstance,
      addLayers,
      removeLayers
    };
  }, [map, deckGlInstance, addLayers, removeLayers]);
  const nLayers = useMemo(() => {
    if (!layers) {
      return null;
    }
    return layers.map(layer => {
      if (layer?.type != null) {
        return /*#__PURE__*/React.cloneElement(layer, {
          interaction: interaction.current,
          map
        });
      }
      return null;
    });
  }, [layers, map]);
  return /*#__PURE__*/React.createElement(MapContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(S.mapContainer, className),
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    ref: mapRef,
    style: {
      width: '100%',
      height: '100%'
    }
  }, map && nLayers, map && children, pointer && pointer.render && pointer.render()), pointer && pointer.getComponents()));
}
export default MapView;